/**
 * R232q5r-hardcore: Permadeath wax-seal scroll.
 *
 * Слушает window event 'aiwari-permadeath' от realmClient.ts когда сервер
 * шлёт сообщение `permadead` (одна смерть на Бессмертнике). Также реагирует
 * на 'aiwari-permadead-locked' — когда login отказан permadead-character'у.
 *
 * Поверх всего рисует пергаментный свиток с восковой печатью «Архив Павших»,
 * имя персонажа, уровень, зону смерти, убийцу. По клику возвращает игрока в
 * character-roster (новый character на reroll).
 */

import { useEffect, useState } from 'react';
import { useUI } from '../state/store.js';
import { disconnect } from '../game/realmClient.js';

interface PermadeathDetail {
  name: string;
  level: number;
  zone: string;
  killer: string;
  ts?: number;
  permadead_at?: string | null;
}

const ZONE_LABELS: Record<string, string> = {
  silver_fields: 'Серебряные Поля',
  blood_mountain: 'Кровогорье',
  ragged_sails_bay: 'Залив Рваных Парусов',
  ash_steppes: 'Ясеневые Степи',
  misty_cliffs: 'Туманные Утёсы',
  salt_mines: 'Соляные Копи',
  thornwood: 'Терновый Лес',
  blackmarsh: 'Чёрные Болота',
  glacier_keep: 'Ледяной Чертог',
  emberhold: 'Тлеющая Твердь',
  dustfall: 'Пыльный Вой',
  hollow_reach: 'Полая Длань',
  unknown: 'Неизвестная земля',
};

export function PermadeathScroll() {
  const setStage = useUI((s) => s.setStage);
  const [detail, setDetail] = useState<PermadeathDetail | null>(null);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    const onPermadead = (e: Event) => {
      const detail = (e as CustomEvent<PermadeathDetail>).detail;
      setDetail(detail);
      setLocked(false);
    };
    const onPermadeadLocked = (e: Event) => {
      const detail = (e as CustomEvent<PermadeathDetail>).detail;
      setDetail(detail);
      setLocked(true);
    };
    window.addEventListener('aiwari-permadeath', onPermadead);
    window.addEventListener('aiwari-permadead-locked', onPermadeadLocked);
    return () => {
      window.removeEventListener('aiwari-permadeath', onPermadead);
      window.removeEventListener('aiwari-permadead-locked', onPermadeadLocked);
    };
  }, []);

  if (!detail) return null;

  const zoneLabel = ZONE_LABELS[detail.zone] ?? detail.zone;
  const killerLabel = detail.killer && detail.killer !== 'unknown' ? detail.killer : 'безымянная угроза';
  const dateLabel = detail.permadead_at
    ? new Date(detail.permadead_at).toLocaleString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : new Date(detail.ts ?? Date.now()).toLocaleString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const onContinue = () => {
    try { sessionStorage.removeItem('aiwari-permadeath'); } catch {}
    try { sessionStorage.removeItem('aiwari-permadeath-locked'); } catch {}
    setDetail(null);
    disconnect();
    setStage('character-roster');
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{
        background: 'radial-gradient(circle at center, rgba(11,9,7,0.86) 0%, rgba(0,0,0,0.96) 70%)',
        backdropFilter: 'blur(6px)',
        animation: 'aiwari-permadeath-fade-in 0.6s ease-out forwards',
      }}
    >
      <style>{`
        @keyframes aiwari-permadeath-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes aiwari-scroll-in {
          0% { transform: translateY(40px) scale(0.92); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes aiwari-seal-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.04); }
        }
      `}</style>

      <div
        className="relative max-w-[640px] w-[92vw] px-12 py-14 text-center"
        style={{
          background: 'linear-gradient(180deg, #E8DCC0 0%, #D4C29C 100%)',
          border: '3px solid #5C4520',
          boxShadow: '0 0 60px rgba(201,169,106,0.3), inset 0 0 40px rgba(92,69,32,0.2)',
          fontFamily: 'EB Garamond, Georgia, serif',
          color: '#3A2818',
          animation: 'aiwari-scroll-in 0.9s cubic-bezier(0.2, 0.7, 0.3, 1) forwards',
        }}
      >
        {/* Corner ornaments */}
        <CornerOrnament position="tl" />
        <CornerOrnament position="tr" />
        <CornerOrnament position="bl" />
        <CornerOrnament position="br" />

        {/* Wax seal */}
        <div
          className="absolute -top-8 left-1/2 -translate-x-1/2 w-20 h-20 rounded-full flex items-center justify-center"
          style={{
            background: 'radial-gradient(circle at 30% 30%, #A33232, #6B1818)',
            border: '3px solid #3A2818',
            boxShadow: '0 4px 12px rgba(0,0,0,0.5), inset 0 -4px 8px rgba(0,0,0,0.4)',
            fontFamily: 'Cinzel, serif',
            fontWeight: 700,
            fontSize: '11px',
            color: '#F2D58A',
            letterSpacing: '0.1em',
            animation: 'aiwari-seal-pulse 2.4s ease-in-out infinite',
          }}
        >
          АРХИВ
        </div>

        <h1
          className="text-[48px] mb-2 mt-6"
          style={{
            fontFamily: 'Cinzel, serif',
            fontWeight: 700,
            color: '#3A2818',
            letterSpacing: '0.04em',
            textShadow: '0 1px 0 rgba(255,255,255,0.4)',
          }}
        >
          {locked ? 'Архив Павших' : 'Один путь окончен'}
        </h1>

        <div
          className="text-[14px] uppercase tracking-[0.2em] mb-8"
          style={{
            fontFamily: 'Spectral SC, serif',
            color: '#8A6B2E',
          }}
        >
          {locked ? 'Имя уже занесено в свитки' : 'Бессмертник принимает дань'}
        </div>

        <p
          className="text-[20px] italic mb-2"
          style={{ fontFamily: 'EB Garamond, serif', color: '#3A2818' }}
        >
          {detail.name}
        </p>
        <p className="text-[13px] mb-8" style={{ color: '#5C4520', fontFamily: 'JetBrains Mono, monospace' }}>
          {detail.level} ур. · пал в {zoneLabel}
        </p>

        <div
          className="my-6 mx-auto h-px"
          style={{
            width: '70%',
            background: 'linear-gradient(90deg, transparent, #8A6B2E, transparent)',
          }}
        />

        <p className="text-[15px] leading-relaxed italic mb-6" style={{ color: '#3A2818' }}>
          {locked
            ? <>Этот воин уже сложил оружие. Его имя выбито в каменной книге Бессмертника, и обратной дороги нет.<br/>Возможно, ему пора уступить место новому путнику.</>
            : <>Душа отделилась от тела. Грозы стихли. Кристаллы умолкли.<br/>На Бессмертнике одна жизнь — она была твоей.</>
          }
        </p>

        <div
          className="text-[12px] mb-8"
          style={{ color: '#9C8E72', fontFamily: 'Spectral SC, serif', letterSpacing: '0.1em' }}
        >
          {dateLabel}
          {killerLabel !== 'безымянная угроза' && <> · от руки {killerLabel}</>}
        </div>

        <button
          type="button"
          onClick={onContinue}
          className="px-10 py-3 transition hover:scale-[1.03]"
          style={{
            background: 'linear-gradient(180deg, #C9A96A, #8A6B2E)',
            color: '#0B0907',
            border: '2px solid #3A2818',
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            fontFamily: 'Cinzel, serif',
            fontWeight: 600,
            fontSize: '14px',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          {locked ? 'К выбору персонажа' : 'Начать заново'}
        </button>
      </div>
    </div>
  );
}

function CornerOrnament({ position }: { position: 'tl' | 'tr' | 'bl' | 'br' }) {
  const transforms: Record<typeof position, string> = {
    tl: 'top-2 left-2',
    tr: 'top-2 right-2 scale-x-[-1]',
    bl: 'bottom-2 left-2 scale-y-[-1]',
    br: 'bottom-2 right-2 scale-x-[-1] scale-y-[-1]',
  };
  return (
    <svg
      className={`absolute ${transforms[position]} w-9 h-9 pointer-events-none`}
      viewBox="0 0 36 36"
      fill="none"
      stroke="#5C4520"
      strokeWidth="1.5"
    >
      <path d="M2 2 L14 2 M2 2 L2 14" />
      <path d="M4 4 Q12 4 12 12" />
      <circle cx="6" cy="6" r="1" fill="#5C4520" />
    </svg>
  );
}
